import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type CSSProperties, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, CheckSquare, Flag, MessageSquare, Paperclip, Receipt, Settings, Zap } from "lucide-react";
import { useStore } from "../lib/store";
import {
  NOTICE_CATEGORY_LABEL,
  unreadCount,
  type Notice,
  type NoticeCategory,
} from "../features/notifications/feed";
import { avatarGradient, avatarInitials } from "../ui/avatar-identity";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { appPath } from "./routes";

/**
 * Popover de notificações do sino, como no protótipo Zênite (`.z-pop.z-notifications`).
 *
 * O conteúdo é o MESMO feed derivado da central (`store.noticeFeed`): não há
 * uma lista guardada à parte para divergir dela (AGENTS §10). Marcar como lida
 * e marcar todas são comandos reais; "Ver detalhes" abre o aviso na central e
 * "Abrir lead" leva ao perfil.
 *
 * Popover NÃO modal: fecha por Escape e clique fora e devolve o foco ao sino.
 */
const TIME_ZONE = "America/Sao_Paulo";

/*
 * CosmosAI não tem aba aqui, por decisão do proprietário: com muitas
 * categorias o menu transbordava. Os avisos da IA continuam em "Todas" e têm
 * a própria caixa na central de notificações.
 */
const TABS: Array<{ id: "all" | NoticeCategory; label: string }> = [
  { id: "all", label: "Todas" },
  { id: "automacao", label: "Funis" },
  { id: "mensagem", label: "Mensagens" },
  { id: "lead", label: "Cadastros" },
  { id: "anexo", label: "Anexos" },
  { id: "tarefa", label: "Tarefas" },
  { id: "financeiro", label: "Financeiro" },
];

/** A estrela de traço da IA não vem do lucide; o desenho é nosso. */
function AiRail({ size }: { size?: number }) {
  return <CosmosAiIcon size={size} variant="outline" />;
}

const KIND_ICON: Record<NoticeCategory, ComponentType<{ size?: number }>> = {
  ia: AiRail,
  lead: Flag,
  mensagem: MessageSquare,
  tarefa: CheckSquare,
  financeiro: Receipt,
  automacao: Zap,
  anexo: Paperclip,
};

const dayFormat = new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "short", timeZone: TIME_ZONE });
const hourFormat = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TIME_ZONE });

/** "16 de set. · 13:41" */
export function noticeStamp(instant: string): string {
  const date = new Date(instant);
  return `${dayFormat.format(date)} · ${hourFormat.format(date)}`;
}

/** "agora", "há 5 min", "há 2 h", "há 3 d" */
export function relativeLabel(instant: string, now: string): string {
  const diff = Math.max(0, new Date(now).getTime() - new Date(instant).getTime());
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return `há ${days} d`;
}

export function NoticePopover({
  open,
  onClose,
  anchorRef,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLButtonElement | null>;
}) {
  const store = useStore();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [tab, setTab] = useState<"all" | NoticeCategory>("all");
  const [position, setPosition] = useState<CSSProperties>({});
  const now = useMemo(() => new Date().toISOString(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const feed = store.noticeFeed();
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    map.set("all", feed.length);
    for (const notice of feed) map.set(notice.category, (map.get(notice.category) ?? 0) + 1);
    return map;
  }, [feed]);
  const visible = tab === "all" ? feed : feed.filter((notice) => notice.category === tab);
  const unread = unreadCount(feed);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const header = anchor?.closest("header");
    if (!anchor || !header) return;
    const anchorRect = anchor.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const width = 440;
    const left = Math.max(16, Math.min(anchorRect.right + 10 - width, window.innerWidth - width - 16));
    setPosition({
      left,
      right: "auto",
      top: headerRect.bottom + 3,
      maxHeight: window.innerHeight - headerRect.bottom - 24,
    });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
      anchorRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchorRef, onClose, open]);

  if (!open) return null;

  const leadOf = (notice: Notice) => store.leads.find((lead) => lead.id === notice.leadId);

  const openDetails = (notice: Notice) => {
    onClose();
    navigate(`${appPath("notificacoes")}?aviso=${encodeURIComponent(notice.key)}`);
  };
  const openLead = (notice: Notice) => {
    if (!notice.leadId) return;
    onClose();
    navigate(`${appPath("leads")}/${notice.leadId}`);
  };

  return (
    <div
      ref={rootRef}
      className="z-pop z-notifications"
      role="region"
      aria-labelledby={titleId}
      style={position}
    >
      <header className="z-notif-head">
        <h3 id={titleId}>Notificações</h3>
        <button
          type="button"
          className="z-notif-tool"
          aria-label="Marcar todas como lidas"
          title="Marcar todas como lidas"
          disabled={unread === 0}
          onClick={() =>
            void store.markAllNoticesRead({
              noticeKeys: feed.filter((notice) => !notice.readAt).map((notice) => notice.key),
            })
          }
        >
          <CheckCheck size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="z-notif-tool"
          aria-label="Preferências de notificações"
          title="Preferências de notificações"
          onClick={() => {
            onClose();
            navigate(`${appPath("notificacoes")}?preferencias=1`);
          }}
        >
          <Settings size={16} aria-hidden="true" />
        </button>
      </header>

      <nav className="z-notif-tabs" aria-label="Filtrar notificações">
        {TABS.filter((item) => item.id === "all" || (counts.get(item.id) ?? 0) > 0).map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={tab === item.id}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            <span className="num">{counts.get(item.id) ?? 0}</span>
          </button>
        ))}
      </nav>

      <div className="z-notif-list">
        {visible.length === 0 ? (
          <div className="z-notif-empty">
            <strong>Nada por aqui</strong>
            {feed.length === 0
              ? "Nenhum aviso em aberto: tarefas, faturas, conversas e leads parados aparecem assim que existirem."
              : "Nenhum aviso desta categoria está em aberto."}
          </div>
        ) : (
          visible.map((notice) => {
            const lead = leadOf(notice);
            const Kind = KIND_ICON[notice.category];
            const name = lead?.name ?? NOTICE_CATEGORY_LABEL[notice.category];
            return (
              <article className="z-notif-item" key={notice.key}>
                <div className="z-notif-avatar">
                  <span className="lead-ava" style={{ background: avatarGradient(name) }} aria-hidden="true">
                    {lead?.photoUrl ? <img src={lead.photoUrl} alt="" /> : avatarInitials(name)}
                  </span>
                  <span className="z-notif-kind" aria-hidden="true">
                    <Kind size={16} />
                  </span>
                </div>
                <div className="z-notif-body">
                  <div className="z-notif-line">
                    <div className="z-notif-title">
                      {lead ? (
                        <button type="button" className="z-notif-name" onClick={() => openLead(notice)}>
                          {lead.name}
                        </button>
                      ) : (
                        <span className="z-notif-name" style={{ cursor: "default" }}>
                          {name}
                        </span>
                      )}{" "}
                      {notice.title}
                      <span className="z-notif-context">
                        <Kind size={16} aria-hidden="true" />
                        {NOTICE_CATEGORY_LABEL[notice.category]}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="z-notif-read"
                      aria-pressed={Boolean(notice.readAt)}
                      aria-label={notice.readAt ? "Marcar como não lida" : "Marcar como lida"}
                      title={notice.readAt ? "Marcar como não lida" : "Marcar como lida"}
                      onClick={() => void store.markNoticeRead({ noticeKey: notice.key, read: !notice.readAt })}
                    >
                      <i aria-hidden="true" />
                    </button>
                  </div>
                  <div className="z-notif-meta">
                    <time dateTime={notice.occurredAt}>{noticeStamp(notice.occurredAt)}</time>
                    <span>{relativeLabel(notice.occurredAt, now)}</span>
                  </div>
                  <div className="z-notif-meta">
                    <span>{notice.detail}</span>
                  </div>
                  <div className="z-notif-actions">
                    <button type="button" className="btn-ghost" onClick={() => openDetails(notice)}>
                      Ver detalhes
                    </button>
                    {lead && (
                      <button type="button" className="btn-ghost" onClick={() => openLead(notice)}>
                        Abrir lead
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>

      <div className="z-notif-foot">
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            onClose();
            navigate(appPath("notificacoes"));
          }}
        >
          <Bell size={14} aria-hidden="true" />
          Abrir a central de notificações
        </button>
      </div>
    </div>
  );
}
